import React from 'react';
import { Badge, Button, StatCard } from '@/components/ui';
import { useNotificationStore } from '@/stores/notificationStore';
import { Users, FileText, DollarSign, Settings, CheckCircle } from 'lucide-react';
import { formatNumber, formatCurrency } from '@/lib/localeFormatters';
import { CodeBlock } from '../CodeBlock';
import { SectionWrapper } from '../SectionWrapper';

export function StatCardsSection() {
  const notifySuccess = useNotificationStore(s => s.success);

  return (
    <SectionWrapper title="Stat Cards">
        <div>
          <h3 className="text-lg font-semibold mb-4">Basic Stat Cards</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Users"
              value={formatNumber(2847)}
              icon={<Users className="w-5 h-5" />}
            />
            <StatCard
              label="Revenue"
              value={formatCurrency(24890.50)}
              valueColor="success"
              iconColor="success"
              icon={<DollarSign className="w-5 h-5" />}
              trend="+12% from last month"
            />
            <StatCard
              label="Active Sessions"
              value={formatNumber(1294)}
              valueColor="warning"
              iconColor="warning"
              icon={<Users className="w-5 h-5" />}
              trend="+5% from yesterday"
            />
            <StatCard
              label="Error Rate"
              value={`${formatNumber(0.8)}%`}
              valueColor="error"
              iconColor="error"
              icon={<FileText className="w-5 h-5" />}
              trend="-2% from last week"
            />
          </div>
          <CodeBlock>
{`<StatCard
  label="Total Users"
  value="2,847"
  icon={<Users className="w-5 h-5" />}
/>

<StatCard
  label="Revenue"
  value="$24,890"
  valueColor="success"
  iconColor="success"
  icon={<DollarSign className="w-5 h-5" />}
  trend="+12% from last month"
/>`}
          </CodeBlock>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Interactive & Loading States</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              label="Clickable Metric"
              value="450"
              icon={<Settings className="w-5 h-5" />}
              onClick={() => notifySuccess('StatCard clicked!')}
              trend="Click me!"
            />
            <StatCard
              label="Loading State"
              value="Loading..."
              loading={true}
              icon={<Users className="w-5 h-5" />}
            />
            <StatCard
              label="Custom Content"
              value={<Badge variant="success">Active</Badge>}
              icon={<CheckCircle className="w-5 h-5" />}
              iconColor="success"
            />
          </div>
          <CodeBlock>
{`// Interactive card
<StatCard
  label="Clickable Metric"
  value="450"
  icon={<Settings className="w-5 h-5" />}
  onClick={() => handleClick()}
  trend="Click me!"
/>

// Loading state
<StatCard
  label="Loading State"
  value="Loading..."
  loading={true}
  icon={<Users className="w-5 h-5" />}
/>

// Custom value content
<StatCard
  label="Custom Content"
  value={<Badge variant="success">Active</Badge>}
  icon={<CheckCircle className="w-5 h-5" />}
  iconColor="success"
/>`}
          </CodeBlock>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Color Variants</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard
              label="Default"
              value="100"
              icon={<Users className="w-5 h-5" />}
              iconColor="default"
            />
            <StatCard
              label="Primary"
              value="200"
              icon={<Users className="w-5 h-5" />}
              iconColor="primary"
            />
            <StatCard
              label="Success"
              value="300"
              valueColor="success"
              icon={<Users className="w-5 h-5" />}
              iconColor="success"
            />
            <StatCard
              label="Warning"
              value="400"
              valueColor="warning"
              icon={<Users className="w-5 h-5" />}
              iconColor="warning"
            />
            <StatCard
              label="Error"
              value="500"
              valueColor="error"
              icon={<Users className="w-5 h-5" />}
              iconColor="error"
            />
          </div>
          <CodeBlock>
{`// Available color variants:
iconColor: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'muted'
valueColor: 'default' | 'success' | 'warning' | 'error' | 'muted'

<StatCard
  label="Success Metric"
  value="95.2%"
  valueColor="success"
  iconColor="success"
  icon={<CheckCircle className="w-5 h-5" />}
/>`}
          </CodeBlock>
        </div>
    </SectionWrapper>
  );
}
